import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { format } from 'date-fns';
import { Calendar, Users, Clock, CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { GET_PROJECTS, GET_PROJECT_POSTS } from '../../graphql/queries';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

interface ProjectPost {
  id: string;
  title: string;
  content: string;
  status: string;
  assignedTo?: string;
  scheduledDate?: string;
  dueDate: string;
  assignedUser?: {
    email: string;
  };
  targets?: Array<{
    platform: string;
  }>;
}

interface ActiveProject {
  id: string;
  name: string;
  plan: string;
  deadline: string;
  status: string;
  progress: number;
  campaignStory: string;
  color: string;
  posts: ProjectPost[];
}

interface ActiveProjectsSectionProps {
  accountId?: string;
}

export function ActiveProjectsSection({ accountId }: ActiveProjectsSectionProps) {
  const navigate = useNavigate();
  const client = useApolloClient();

  const { accountId: contextAccountId } = useAccount();
  const effectiveAccountId = accountId || contextAccountId;

  // Fetch all projects
  const { data: projectsData, loading: projectsLoading } = useQuery(GET_PROJECTS, {
    variables: { accountId: effectiveAccountId },
    skip: !effectiveAccountId,
  });

  // Filter to include planning, active, and review projects
  const activeProjects = useMemo(() => {
    if (!projectsData?.projects) {
      return [];
    }

    return projectsData.projects.filter((p: any) => {
      if (!p.status) {
        return false;
      }
      const status = p.status.toLowerCase().trim();
      return ['planning', 'active', 'review'].includes(status);
    });
  }, [projectsData]);

  // State to store posts for each project
  const [projectPostsMap, setProjectPostsMap] = useState<Record<string, ProjectPost[]>>({});
  const [postsLoading, setPostsLoading] = useState(false);

  // Fetch project posts for all active projects
  useEffect(() => {
    if (activeProjects.length === 0) {
      setProjectPostsMap({});
      return;
    }

    setPostsLoading(true);
    const fetchAllPosts = async () => {
      try {
        const postsPromises = activeProjects.map(async (project: any) => {
          try {
            const { data } = await client.query({
              query: GET_PROJECT_POSTS,
              variables: { projectId: project.id },
            });

            const rawPosts = data?.projectPosts || [];
            const workInProgressPosts: ProjectPost[] = rawPosts
              .filter((p: any) => {
                const status = p.status?.toLowerCase().replace(/_/g, '-');
                return ['draft', 'in-review', 'approved'].includes(status);
              })
              .map((p: any) => ({
                id: p.id,
                title: p.title,
                content: p.content,
                status: p.status?.toLowerCase().replace(/_/g, '-') || 'draft',
                assignedTo: p.assignedUser?.email,
                scheduledDate: p.scheduledDate,
                dueDate: p.dueDate,
                assignedUser: p.assignedUser,
                targets: p.targets || [],
              }));

            return { projectId: project.id, posts: workInProgressPosts };
          } catch (error) {
            console.error(`Error fetching posts for project ${project.id}:`, error);
            return { projectId: project.id, posts: [] };
          }
        });

        const results = await Promise.all(postsPromises);
        const newMap: Record<string, ProjectPost[]> = {};
        results.forEach(({ projectId, posts }) => {
          newMap[projectId] = posts;
        });
        setProjectPostsMap(newMap);
      } catch (error) {
        console.error('Error fetching project posts:', error);
      } finally {
        setPostsLoading(false);
      }
    };

    fetchAllPosts();
  }, [activeProjects, client]);

  // Transform data
  const activeProjectsWithPosts: ActiveProject[] = useMemo(() => {
    return activeProjects.map((project: any) => {
      const posts = projectPostsMap[project.id] || [];

      return {
        id: project.id,
        name: project.name,
        plan: project.plan || '',
        deadline: project.deadline,
        campaignStory: project.campaignStory || '',
        status: project.status?.toLowerCase() || 'active',
        progress: project.progress || 0,
        color: project.color || 'bg-blue-500',
        posts,
      };
    });
  }, [activeProjects, projectPostsMap]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="w-4 h-4 text-blue-600" />;
      case 'in-review':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'draft':
        return <Circle className="w-4 h-4 text-cmp-ink-2" />;
      default:
        return <Circle className="w-4 h-4 text-cmp-ink-2" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'in-review':
        return 'bg-yellow-100 text-yellow-700';
      case 'draft':
        return 'bg-cmp-surface text-cmp-ink-2';
      default:
        return 'bg-cmp-surface text-cmp-ink-2';
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
      case 'x':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'linkedin':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'instagram':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'facebook':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'youtube':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-cmp-surface text-cmp-ink-2';
    }
  };

  if (projectsLoading) {
    return (
      <Card className="bg-core-dark border-0 rounded-none">
        <CardHeader className="px-6 pt-6">
          <CardTitle className="text-xl font-semibold text-white">Active Projects</CardTitle>
        </CardHeader>
        <CardContent className="px-6 pb-6">
          <Loader label="Loading projects" className="text-cmp-ink-2" />
        </CardContent>
      </Card>
    );
  }

  if (activeProjectsWithPosts.length === 0) {
    return (
      <Card className="bg-core-dark border-0 rounded-none">
        <CardHeader className="px-6 pt-6 pb-0">
          <CardTitle className="text-xl font-semibold text-white">Active Projects</CardTitle>
          <CardDescription className="text-cmp-ink-2">
            Projects you're currently working on
          </CardDescription>
        </CardHeader>
        <CardContent className="px-6 pb-6">
          <p className="text-cmp-ink-2">No active projects at the moment.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-core-dark border-0 h-full flex flex-col rounded-none">
      <CardHeader className="flex-shrink-0 px-6 pt-6 pb-0">
        <CardTitle className="text-xl font-semibold text-white">Active Projects</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 flex-1 min-h-0 overflow-y-auto px-6 pb-6">
        {postsLoading && activeProjectsWithPosts.length === 0 && (
          <Loader label="Loading project posts" className="text-cmp-ink-2 text-center py-4" />
        )}
        {activeProjectsWithPosts.map((project) => (
          <div
            key={project.id}
            className="bg-panel rounded-lg p-4 border border-cmp-line hover:border-primary-light transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold">{project.name}</h3>
                  <Badge
                    className={
                      project.status === 'active'
                        ? 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line'
                        : project.status === 'planning'
                          ? 'bg-cmp-surface text-cmp-ink-2'
                          : 'bg-yellow-100 text-yellow-700'
                    }
                    variant="secondary"
                  >
                    {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                  </Badge>
                </div>
                {project.campaignStory && (
                  <p className="text-cmp-ink-2 text-sm mb-2">{project.campaignStory}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-cmp-ink-2">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>Due: {format(new Date(project.deadline), 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{project.posts.length} posts in progress</span>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-cmp-ink-2">Progress</span>
                    <span className="text-xs text-cmp-ink-2">{project.progress}%</span>
                  </div>
                  <Progress value={project.progress} className="h-2" />
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/projects')}
                className="text-primary-light hover:text-white"
              >
                View
              </Button>
            </div>

            {project.posts.length > 0 && (
              <div className="mt-4 pt-4 border-t border-form-dark">
                <h4 className="text-sm font-medium text-white mb-2">Posts in Progress</h4>
                <div className="space-y-2">
                  {project.posts.slice(0, 3).map((post) => (
                    <div
                      key={post.id}
                      className="bg-form-dark rounded p-2 flex items-start justify-between gap-2"
                    >
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        {getStatusIcon(post.status)}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{post.title}</p>
                          <p className="text-cmp-ink-2 text-xs line-clamp-1">{post.content}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge
                              className={`${getStatusColor(post.status)} text-xs`}
                              variant="secondary"
                            >
                              {post.status}
                            </Badge>
                            {post.targets && post.targets.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {post.targets.slice(0, 2).map((target, idx) => (
                                  <Badge
                                    key={idx}
                                    className={`${getPlatformColor(target.platform)} text-xs`}
                                    variant="secondary"
                                  >
                                    {target.platform}
                                  </Badge>
                                ))}
                                {post.targets.length > 2 && (
                                  <Badge
                                    className="bg-cmp-surface text-cmp-ink-2 text-xs"
                                    variant="secondary"
                                  >
                                    +{post.targets.length - 2}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {post.dueDate && (
                        <div className="flex items-center gap-1 text-xs text-cmp-ink-2 flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          <span>{format(new Date(post.dueDate), 'MMM d')}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {project.posts.length > 3 && (
                    <p className="text-xs text-cmp-ink-2 text-center pt-1">
                      +{project.posts.length - 3} more posts
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
